import axios, { AxiosError, AxiosRequestConfig, Method } from "axios";
import { translate } from "ui/i18n";
import { Snackbar } from "ui/pages/snackbar";
import { removeSessionInLocalStorage } from "data/auth/auth-session-storage";

const instance = axios.create();

instance.interceptors.response.use(null, onRequestError);

type RequestOptions = {
  params?: unknown;
  api?: "core" | "connect" | "elastic-service" | "ai-service";
  includeAuthorization?: boolean;
  additionalHeaders?: Record<string, string>;
};

const defaultRequestOptions: RequestOptions = {
  params: {},
  api: "core",
  includeAuthorization: true,
  additionalHeaders: {},
};

let retriesCount = 0;

export class HttpService {
  private static accessToken: string = "";
  private static lastRequestParams: {
    api: "core" | "connect" | "elastic-service" | "ai-service";
    url: string;
    method: Method;
    data?: any;
    params?: any;
    includeAuthorization?: boolean;
    additionalHeaders?: Record<string, string>;
  };

  static setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
  }

  static async get(
    url: string,
    options: RequestOptions = defaultRequestOptions
  ) {
    const response = this.request(
      options.api,
      url,
      "GET",
      null,
      options.params,
      options.includeAuthorization,
      options.additionalHeaders
    );
    return response;
  }

  static async post(
    url: string,
    data: unknown,
    options: RequestOptions = defaultRequestOptions
  ) {
    const response = this.request(
      options.api,
      url,
      "POST",
      data,
      options.params,
      options.includeAuthorization,
      options.additionalHeaders
    );
    return response;
  }

  static async patch(
    url: string,
    data: unknown,
    options: RequestOptions = defaultRequestOptions
  ) {
    const response = this.request(
      options.api,
      url,
      "PATCH",
      data,
      options.params,
      options.includeAuthorization,
      options.additionalHeaders
    );
    return response;
  }

  static async delete(
    url: string,
    options: RequestOptions = defaultRequestOptions
  ) {
    const response = this.request(
      options.api,
      url,
      "DELETE",
      null,
      options.params,
      options.includeAuthorization,
      options.additionalHeaders
    );
    return response;
  }

  private static async request(
    api: "core" | "connect" | "elastic-service" | "ai-service",
    url: string,
    method: Method,
    data?: unknown,
    params?: unknown,
    includeAuthorization = true,
    additionalHeaders?: Record<string, string>
  ) {
    const config: AxiosRequestConfig = {};
    config.url = getUrl(api, url);
    config.method = method;
    config.data = data;
    config.params = params;
    config.headers = {};
    this.lastRequestParams = {
      api,
      url,
      method,
      data,
      params,
      includeAuthorization,
      additionalHeaders,
    };
    retriesCount = 0;

    if (includeAuthorization) {
      config.headers = { Authorization: `Bearer ${this.accessToken}` };
    }

    if (additionalHeaders) {
      config.headers = { ...config.headers, ...additionalHeaders };
    }

    return await instance.request(config);
  }

  static async requestAgainIfError() {
    if (this.lastRequestParams) {
      const config: AxiosRequestConfig = {};
      config.url = getUrl(
        this.lastRequestParams.api,
        this.lastRequestParams.url
      );
      config.method = this.lastRequestParams.method;
      config.data = this.lastRequestParams.data;
      config.params = this.lastRequestParams.params;
      config.headers = {};

      if (this.lastRequestParams.includeAuthorization) {
        config.headers = { Authorization: `Bearer ${this.accessToken}` };
      }

      if (this.lastRequestParams.additionalHeaders) {
        config.headers = {
          ...config.headers,
          ...this.lastRequestParams.additionalHeaders,
        };
      }

      retriesCount++;

      return await instance.request(config);
    }
  }
}

function onRequestError(error: AxiosError) {
  switch (error.response?.status) {
    case 401:
      if (
        (error.response.data as { data: { name: string } }).data.name ===
        "TokenExpiredError"
      )
        Snackbar.showError({
          title: translate("ns1:Errors.SessionTimedOut"),
        });
      removeSessionInLocalStorage();
      window.location.href = "/auth";
      break;
    case 403:
      // window.location.href = "/";
      break;
    case 500:
    case 501:
      if (retriesCount < 2) {
        setTimeout(
          () => HttpService.requestAgainIfError().catch(console.log),
          1000
        );
      }
      break;
  }
  return Promise.reject(error);
}

function getUrl(
  api: "core" | "connect" | "elastic-service" | "ai-service",
  url: string
) {
  switch (api) {
    case "core":
      return `${process.env.REACT_APP_CORE_URL}/${url}`;
    case "connect":
      return `${process.env.REACT_APP_CONNECT_URL}/${url}`;
    case "elastic-service":
      return `${process.env.REACT_APP_ELASTIC_SERVICE_URL}/${url}`;
    case "ai-service":
      return `${process.env.REACT_APP_AI_SERVICE_URL}/${url}`;
    default:
      return `${process.env.REACT_APP_CORE_URL}/${url}`;
  }
}
